import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search } from "lucide-react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "./AdminOverview";

export default function AdminQuotes() {
    const [quotes, setQuotes] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");

    useEffect(() => { load(); }, [statusFilter]);

    const load = async () => {
        const params = statusFilter !== "all" ? { status: statusFilter } : {};
        const { data } = await api.get("/admin/quotes", { params });
        setQuotes(data);
    };

    const filtered = quotes.filter((q) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (q.reference || "").toLowerCase().includes(s)
            || (q.customer?.name || "").toLowerCase().includes(s)
            || (q.customer?.email || "").toLowerCase().includes(s);
    });

    const exportCsv = () => {
        const token = localStorage.getItem("ab_token");
        window.open(`${API}/admin/quotes/export/csv?token=${token}`, "_blank");
    };

    return (
        <div className="space-y-6" data-testid="admin-quotes">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">Pedidos de orçamento</h1>
                    <p className="text-white/60">{filtered.length} pedidos</p>
                </div>
                <Button variant="outline" onClick={exportCsv} data-testid="export-csv">
                    <Download className="w-4 h-4 mr-2" /> Exportar CSV
                </Button>
            </div>

            <div className="flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <Input placeholder="Procurar por referência, nome ou email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-quotes" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos os estados</SelectItem>
                        <SelectItem value="novo">Novo</SelectItem>
                        <SelectItem value="em_analise">Em análise</SelectItem>
                        <SelectItem value="orcamento_enviado">Orçamento enviado</SelectItem>
                        <SelectItem value="aceite">Aceite</SelectItem>
                        <SelectItem value="agendado">Agendado</SelectItem>
                        <SelectItem value="concluido">Concluído</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="ab-card overflow-hidden">
                <table className="w-full">
                    <thead className="bg-[#171717] border-b border-[#2A2A2A] text-xs font-medium uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left p-4">Referência</th>
                            <th className="text-left p-4">Cliente</th>
                            <th className="text-left p-4 hidden md:table-cell">Localidade</th>
                            <th className="text-right p-4">Total</th>
                            <th className="text-left p-4">Estado</th>
                            <th className="text-left p-4 hidden lg:table-cell">Data</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2A2A2A]">
                        {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="text-center p-10 text-white/50">Sem pedidos.</td></tr>
                        ) : filtered.map((q) => (
                            <tr key={q.id} className="hover:bg-[#171717] cursor-pointer" onClick={() => window.location.assign(`/admin/pedidos/${q.id}`)} data-testid={`quote-row-${q.reference}`}>
                                <td className="p-4 font-mono text-sm">{q.reference}</td>
                                <td className="p-4">
                                    <div className="font-medium">{q.customer?.name}</div>
                                    <div className="text-xs text-white/50">{q.customer?.email}</div>
                                </td>
                                <td className="p-4 text-sm hidden md:table-cell">{q.location?.locality} · {q.location?.district}</td>
                                <td className="p-4 text-right font-heading font-bold">{q.calculation?.total?.toFixed(2)} €</td>
                                <td className="p-4"><StatusBadge status={q.status} /></td>
                                <td className="p-4 text-sm text-white/50 hidden lg:table-cell">{new Date(q.created_at).toLocaleDateString("pt-PT")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
